import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabaseClient'
import { SUBJECT_OPTIONS, getDefaultSubject, isValidSubject } from '../../constants/subjects'
import {
  getSuggestedReleaseDate,
  validateReleaseDateConsistency,
  getMonthNameSk,
  SCHOOL_YEAR_MONTHS,
} from '../../utils/testValidation'
import { isValidHttpUrl } from '../../utils/videoExplanation'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

/**
 * Generate school year options dynamically
 * Returns current school year + next 10 years
 */
const generateSchoolYearOptions = () => {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1 // 1-12
  
  // School year starts in September (month 9)
  // If current month is Jan-Aug, we're still in previous school year
  const startYear = currentMonth >= 9 ? currentYear : currentYear - 1
  
  const options = []
  for (let i = 0; i < 10; i++) {
    const year = startYear + i
    options.push(`${year}/${year + 1}`)
  }
  
  return options
}

const AdminTestForm = () => {
  const navigate = useNavigate()
  const { testId } = useParams()
  const { user, isAdmin, adminChecked } = useAuth()
  const isEdit = Boolean(testId)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    intro_text: '',
    topics_text: '',
    subject: getDefaultSubject(),  // Uses SUBJECT_OPTIONS constant
    school_year: '2025/2026',
    release_order: 1,
    release_month: 9,
    release_date: '',
    duration_minutes: 45,
    is_published: true,
    is_free: false,
    admin_only: false,
    // Optional per-test explanation video (backing columns:
    // tests.video_explanation_url, tests.video_explanation_title).
    // Empty string means "no video"; the results page renders nothing.
    video_explanation_url: '',
    video_explanation_title: ''
  })
  
  // Guard to prevent re-initialization while user is editing
  const [hasInitialized, setHasInitialized] = useState(false)

  useEffect(() => {
    if (adminChecked && !isAdmin) {
      navigate('/dashboard')
    }
  }, [isAdmin, adminChecked, navigate])

  useEffect(() => {
    if (isEdit && user && isAdmin && !hasInitialized) {
      fetchTest()
    }
  }, [isEdit, user, isAdmin, hasInitialized])

  const fetchTest = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      const response = await fetch(`${BACKEND_URL}/api/admin/tests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const tests = await response.json()
        const test = tests.find(t => t.id === testId)
        if (test) {
          setFormData({
            title: test.title || '',
            description: test.description || '',
            intro_text: test.intro_text || '',
            topics_text: test.topics_text || '',
            subject: test.subject || getDefaultSubject(),
            school_year: test.school_year || '2025/2026',
            release_order: test.release_order || 1,
            release_month: test.release_month || 9,
            release_date: test.release_date || '',
            duration_minutes: test.duration_minutes || 45,
            is_published: test.is_published !== false,
            is_free: test.is_free === true,
            admin_only: test.admin_only === true,
            // Load existing values; treat null/undefined as empty strings so
            // the inputs behave as "no video" and existing tests stay unchanged.
            video_explanation_url: test.video_explanation_url || '',
            video_explanation_title: test.video_explanation_title || ''
          })
          setHasInitialized(true) // Mark as initialized
        }
      }
    } catch (err) {
      setError('Chyba pri načítaní testu')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      // Normalize and validate payload
      const normalizedSubject = formData.subject?.trim().toLowerCase() || getDefaultSubject()
      
      const normalizedPayload = {
        title: formData.title?.trim() || '',
        description: formData.description?.trim() || '',
        intro_text: formData.intro_text?.trim() || '',
        topics_text: formData.topics_text?.trim() || '',
        subject: normalizedSubject,
        school_year: formData.school_year?.trim() || '2025/2026',
        release_order: parseInt(formData.release_order) || 1,
        release_month: parseInt(formData.release_month) || 9,
        // release_date is required by DB - use first day of release_month if not provided
        release_date: formData.release_date?.trim() || 
          `${formData.school_year?.split('/')[0]}-${String(parseInt(formData.release_month) || 9).padStart(2, '0')}-01`,
        duration_minutes: parseInt(formData.duration_minutes) || 45,
        is_published: Boolean(formData.is_published),
        is_free: Boolean(formData.is_free),
        admin_only: Boolean(formData.admin_only),
        // Optional video fields: send trimmed strings if non-empty, else null
        // so the DB stores NULL and existing tests without a video keep
        // behaving exactly as before.
        video_explanation_url: (formData.video_explanation_url || '').trim() || null,
        video_explanation_title: (formData.video_explanation_title || '').trim() || null
      }

      // Validation
      if (!normalizedPayload.title) {
        setError('Názov testu je povinný')
        setLoading(false)
        return
      }

      if (!isValidSubject(normalizedPayload.subject)) {
        setError(`Neplatný predmet: ${normalizedPayload.subject}`)
        setLoading(false)
        return
      }

      // release_order: positive integer (display order only)
      if (normalizedPayload.release_order < 1) {
        setError('Poradie musí byť kladné celé číslo.')
        setLoading(false)
        return
      }

      // release_month: calendar month 1-12
      const month = normalizedPayload.release_month
      if (!Number.isInteger(month) || month < 1 || month > 12) {
        setError('Mesiac vydania musí byť medzi 1 a 12.')
        setLoading(false)
        return
      }

      // Optional video URL: if provided, must be a valid http(s) URL.
      if (normalizedPayload.video_explanation_url && !isValidHttpUrl(normalizedPayload.video_explanation_url)) {
        setError('URL videa musí byť platná http:// alebo https:// adresa.')
        setLoading(false)
        return
      }

      console.log('[AdminTestForm] 📤 Outgoing payload:', normalizedPayload)
      console.log('[AdminTestForm] ✅ Validation passed')

      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      const url = isEdit 
        ? `${BACKEND_URL}/api/admin/tests/${testId}`
        : `${BACKEND_URL}/api/admin/tests`

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(normalizedPayload)
      })

      console.log('[AdminTestForm] 📥 Response status:', response.status)

      // CRITICAL: Read response body EXACTLY ONCE
      // Store in variable and reuse - never call .text() or .json() again
      let responseText = ''
      let responseData = null
      
      try {
        responseText = await response.text()
        console.log('[AdminTestForm] 📄 Raw response:', responseText.substring(0, 200))
        
        // Try to parse as JSON
        try {
          responseData = JSON.parse(responseText)
        } catch (parseError) {
          console.error('[AdminTestForm] ⚠️ JSON parse failed:', parseError.message)
          responseData = { detail: responseText }
        }
      } catch (readError) {
        console.error('[AdminTestForm] ❌ Failed to read response:', readError.message)
        setError('Chyba pri čítaní odpovede zo servera')
        setLoading(false)
        return
      }
      
      if (response.ok) {
        console.log('[AdminTestForm] ✅ Success:', responseData)
        setSuccess(isEdit ? 'Test bol upravený' : 'Test bol vytvorený')
        
        // For new test, redirect to question management
        // For edit, go back to test list
        if (isEdit) {
          setTimeout(() => navigate('/admin/tests'), 1500)
        } else {
          // Get the created test ID and redirect to add questions
          const newTestId = responseData?.id
          if (newTestId) {
            setTimeout(() => navigate(`/admin/tests/${newTestId}/questions`), 1500)
          }
        }
      } else {
        // Handle error response
        console.error('[AdminTestForm] ❌ Backend error:', responseData)
        console.error('[AdminTestForm] ❌ Status:', response.status)
        console.error('[AdminTestForm] ❌ Full response:', responseText)
        
        // Extract and localize error message
        let errorMessage = 'Chyba pri ukladaní testu'
        
        if (responseData && responseData.detail) {
          const detail = responseData.detail
          
          // User-friendly Slovak messages for common errors
          if (typeof detail === 'string') {
            if (detail.includes('already exists') || detail.includes('unique constraint')) {
              errorMessage = 'Test s týmto školským rokom, predmetom a poradím už existuje. Zvoľte iné poradie.'
            } else if (detail.includes('release_month_check') || detail.includes('release_month must be')) {
              errorMessage = 'Mesiac vydania musí byť medzi 1 a 12.'
            } else if (detail.includes('release_order_check') || detail.includes('release_order must be')) {
              errorMessage = 'Poradie musí byť kladné celé číslo.'
            } else if (detail.includes('subject_check') || detail.includes('Invalid subject')) {
              errorMessage = 'Neplatný predmet. Povolené: Matematika, Slovenčina'
            } else {
              // Show original message if not recognized
              errorMessage = detail
            }
          } else if (typeof detail === 'object') {
            errorMessage = JSON.stringify(detail)
          }
        }
        
        setError(errorMessage)
      }
    } catch (err) {
      console.error('[AdminTestForm] ❌ Exception:', err)
      setError(`Chyba pri ukladaní testu: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  if (!adminChecked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!isAdmin) return null

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-blue-600 text-white py-6 mb-8">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">{isEdit ? 'Upraviť test' : 'Vytvoriť nový test'}</h1>
          </div>
          <button
            onClick={() => navigate('/admin/tests')}
            className="px-4 py-2 bg-blue-700 hover:bg-blue-800 rounded-lg transition"
          >
            ← Späť na testy
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Názov testu *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="napr. Test 1 - Matematika"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Popis
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Krátke zhrnutie testu
              <span className="text-xs text-gray-500 ml-2">(zobrazí sa na úvodnej stránke testu)</span>
            </label>
            <textarea
              value={formData.intro_text}
              onChange={(e) => setFormData({...formData, intro_text: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="4"
              placeholder="V tomto teste si precvičíš..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Témy testu
              <span className="text-xs text-gray-500 ml-2">(zoznam tém, ktoré test pokrýva)</span>
            </label>
            <textarea
              value={formData.topics_text}
              onChange={(e) => setFormData({...formData, topics_text: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              placeholder="Prídavné mená, vybrané slová, pravopis..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Predmet *
              </label>
              <select
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {SUBJECT_OPTIONS.map(subject => (
                  <option key={subject.value} value={subject.value}>
                    {subject.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Školský rok *
              </label>
              <select
                value={formData.school_year}
                onChange={(e) => setFormData({...formData, school_year: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {generateSchoolYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Poradie *
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.release_order}
                onChange={(e) => setFormData({...formData, release_order: parseInt(e.target.value)})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="test-form-release-order"
              />
              <p className="text-xs text-gray-500 mt-1">
                Poradie určuje zobrazenie testu v zozname. Neurčuje mesiac ani dátum vydania.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mesiac vydania *
              </label>
              <select
                value={formData.release_month}
                onChange={(e) => setFormData({...formData, release_month: parseInt(e.target.value)})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="test-form-release-month"
              >
                {SCHOOL_YEAR_MONTHS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Zvolený kalendárny mesiac testu.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trvanie (min) *
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dátum vydania *
            </label>
            <input
              type="date"
              required
              value={formData.release_date}
              onChange={(e) => setFormData({...formData, release_date: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {(() => {
              const validation = validateReleaseDateConsistency(
                formData.school_year,
                formData.release_month,
                formData.release_date
              )
              const suggested = getSuggestedReleaseDate(formData.school_year, formData.release_month)
              const monthName = getMonthNameSk(formData.release_month)
              
              return (
                <div className="mt-2 space-y-1">
                  {!validation.valid && validation.message && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <span>⚠️</span>
                      <span>{validation.message}</span>
                    </p>
                  )}
                  {suggested && validation.valid && (
                    <p className="text-sm text-green-600">
                      ✅ Dátum v zvolenom mesiaci ({monthName}). Prvý deň mesiaca: {suggested}
                    </p>
                  )}
                  {suggested && !formData.release_date && (
                    <p className="text-sm text-blue-600">
                      💡 Odporúčaný: {suggested}
                    </p>
                  )}
                </div>
              )
            })()}
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_published}
                onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                data-testid="test-form-is-published"
              />
              <span className="text-sm font-medium text-gray-700">Publikovať test</span>
            </label>
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_free}
                onChange={(e) => setFormData({...formData, is_free: e.target.checked})}
                className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                data-testid="test-form-is-free"
              />
              <span className="text-sm font-medium text-gray-700">Zadarmo pre všetkých prihlásených</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Ak je vypnuté, test je dostupný iba používateľom s plateným prístupom pre daný školský rok.
            </p>
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.admin_only}
                onChange={(e) => setFormData({...formData, admin_only: e.target.checked})}
                className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                data-testid="test-form-admin-only"
              />
              <span className="text-sm font-medium text-gray-700">Len pre admin (náhľad)</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Ak je zapnuté, test uvidia v zozname iba administrátori (s odznakom „ADMIN náhľad"). Bežní používatelia test neuvidia a nebudú mať prístup ani k detailu ani k otázkam. Nezávisí od archivácie.
            </p>
          </div>

          {/* Optional per-test explanation video (video_explanation_url +
              video_explanation_title). Both fields are optional; when
              empty, the results page renders no video UI. */}
          <div className="border-t pt-6 space-y-4">
            <div>
              <label
                htmlFor="test-form-video-url"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Video s vysvetlením – URL
                <span className="text-xs text-gray-500 ml-2">(voliteľné)</span>
              </label>
              <input
                id="test-form-video-url"
                type="url"
                inputMode="url"
                value={formData.video_explanation_url}
                onChange={(e) => setFormData({...formData, video_explanation_url: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://www.youtube.com/watch?v=..."
                data-testid="test-form-video-url"
              />
            </div>

            <div>
              <label
                htmlFor="test-form-video-title"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Názov videa
                <span className="text-xs text-gray-500 ml-2">(voliteľné)</span>
              </label>
              <input
                id="test-form-video-title"
                type="text"
                value={formData.video_explanation_title}
                onChange={(e) => setFormData({...formData, video_explanation_title: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Vysvetlenie príkladov – Test 3"
                data-testid="test-form-video-title"
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium"
            >
              {loading ? 'Ukladám...' : (isEdit ? 'Uložiť zmeny' : 'Vytvoriť test')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/tests')}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
            >
              Zrušiť
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AdminTestForm
